import React from "react";

/**
 * DoWe robot mascot — Daft-Punk LED edition with rocket booster 🚀
 * Glossy chrome helmet, LED gradient eyes & smile, Wedo W mark centered,
 * No arms, rocket booster with animated flame below the body.
 */
export default function DoWeRobot({ size = 220, className = "" }) {
  const ink = "#1C1580";
  const accent = "#E62C86";
  const lime = "#9DBC1D";

  return (
    <svg
      viewBox="0 0 260 360"
      width={size}
      height={(size * 360) / 260}
      className={className}
      aria-label="Do.We · Compagnon IA Wedo"
      role="img"
    >
      <defs>
        {/* Glossy chrome shell */}
        <linearGradient id="shell" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="55%" stopColor="#EDEAE0" />
          <stop offset="100%" stopColor="#CFCBBC" />
        </linearGradient>
        <linearGradient id="shellSide" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.7" />
          <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.7" />
        </linearGradient>

        {/* Visor : deep gradient navy */}
        <linearGradient id="visor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#15104D" />
          <stop offset="50%" stopColor="#1C1580" />
          <stop offset="100%" stopColor="#3a1d8a" />
        </linearGradient>
        <linearGradient id="visorGloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.32" />
          <stop offset="40%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        {/* Chest screen */}
        <linearGradient id="chest" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1C1580" />
          <stop offset="100%" stopColor="#15104D" />
        </linearGradient>

        {/* === LED gradients === */}
        <linearGradient id="ledEye" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#EEE316" />
          <stop offset="50%" stopColor="#9DBC1D" />
          <stop offset="100%" stopColor="#52A385" />
        </linearGradient>
        <linearGradient id="ledMouth" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#E62C86" />
          <stop offset="33%" stopColor="#F3A114" />
          <stop offset="66%" stopColor="#EEE316" />
          <stop offset="100%" stopColor="#9DBC1D" />
        </linearGradient>
        <linearGradient id="neonStrip" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#E62C86" />
          <stop offset="33%" stopColor="#F3A114" />
          <stop offset="66%" stopColor="#EEE316" />
          <stop offset="100%" stopColor="#9DBC1D" />
        </linearGradient>

        {/* Rocket flame */}
        <linearGradient id="flame" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="20%" stopColor="#EEE316" />
          <stop offset="55%" stopColor="#F3A114" />
          <stop offset="100%" stopColor="#E62C86" stopOpacity="0" />
        </linearGradient>

        <filter id="softShadow" x="-20%" y="-10%" width="140%" height="130%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
          <feOffset dx="0" dy="5" />
          <feComponentTransfer><feFuncA type="linear" slope="0.2" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="flameBlur">
          <feGaussianBlur stdDeviation="2" />
        </filter>
      </defs>

      {/* Floor reflection / launch pad */}
      <ellipse cx="130" cy="354" rx="50" ry="4" fill={ink} opacity="0.15">
        <animate attributeName="rx" values="48;54;48" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.12;0.2;0.12" dur="1.6s" repeatCount="indefinite" />
      </ellipse>

      {/* Antenna */}
      <g>
        <line x1="130" y1="14" x2="130" y2="40" stroke={ink} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="130" cy="11" r="14" fill={accent} opacity="0.18">
          <animate attributeName="r" values="11;16;11" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.25;0;0.25" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="130" cy="11" r="5" fill={accent} />
        <circle cx="129" cy="9.5" r="1.6" fill="#FFFFFF" opacity="0.85" />
      </g>

      {/* === HEAD === */}
      <g filter="url(#softShadow)">
        <path
          d="M 60 76 Q 60 44, 100 44 L 160 44 Q 200 44, 200 76 L 200 142 Q 200 168, 174 168 L 86 168 Q 60 168, 60 142 Z"
          fill="url(#shell)"
          stroke={ink}
          strokeWidth="2.5"
        />
        <path
          d="M 60 76 Q 60 44, 100 44 L 160 44 Q 200 44, 200 76 L 200 142 Q 200 168, 174 168 L 86 168 Q 60 168, 60 142 Z"
          fill="url(#shellSide)"
        />
        <path d="M 70 76 Q 70 56, 100 56 L 160 56 Q 190 56, 190 76" fill="none" stroke={ink} strokeWidth="1" opacity="0.3" />
        <rect x="126" y="48" width="8" height="14" rx="2" fill={ink} opacity="0.35" />

        {/* Visor — centered (74..186, center 130) */}
        <rect x="74" y="78" width="112" height="72" rx="22" fill="url(#visor)" />
        <rect x="74" y="78" width="112" height="72" rx="22" fill="none" stroke={ink} strokeWidth="0.8" opacity="0.5" />
        <rect x="76" y="80" width="108" height="34" rx="20" fill="url(#visorGloss)" />

        {/* === LED EYES — Daft Punk style, rounded squares with gradient === */}
        <g filter="url(#glow)">
          <rect x="96" y="98" width="20" height="20" rx="5" fill="url(#ledEye)">
            <animate attributeName="opacity" values="1;0.6;1" dur="3.2s" repeatCount="indefinite" />
          </rect>
          <rect x="144" y="98" width="20" height="20" rx="5" fill="url(#ledEye)">
            <animate attributeName="opacity" values="1;0.6;1" dur="3.2s" repeatCount="indefinite" />
          </rect>
        </g>
        {/* Eye glossy highlights */}
        <circle cx="103" cy="104" r="3" fill="#FFFFFF" opacity="0.8" />
        <circle cx="151" cy="104" r="3" fill="#FFFFFF" opacity="0.8" />

        {/* === LED MOUTH — gradient smile === */}
        <g filter="url(#glow)">
          <path
            d="M 110 132 Q 130 146, 150 132"
            stroke="url(#ledMouth)"
            strokeWidth="4.5"
            strokeLinecap="round"
            fill="none"
          />
        </g>

        {/* Side audio ports */}
        <rect x="54" y="100" width="6" height="36" rx="3" fill={ink} />
        <rect x="56" y="106" width="2" height="5" rx="1" fill={accent} />
        <rect x="56" y="115" width="2" height="5" rx="1" fill={accent} opacity="0.6" />
        <rect x="56" y="124" width="2" height="5" rx="1" fill={accent} opacity="0.35" />

        <rect x="200" y="100" width="6" height="36" rx="3" fill={ink} />
        <rect x="202" y="106" width="2" height="5" rx="1" fill={accent} />
        <rect x="202" y="115" width="2" height="5" rx="1" fill={accent} opacity="0.6" />
        <rect x="202" y="124" width="2" height="5" rx="1" fill={accent} opacity="0.35" />
      </g>

      {/* Neck */}
      <rect x="116" y="168" width="28" height="12" rx="3" fill={ink} opacity="0.9" />
      <rect x="110" y="178" width="40" height="4" rx="2" fill={ink} opacity="0.6" />

      {/* === BODY — aligned width with head (60..200), elongated for harmony === */}
      <g filter="url(#softShadow)">
        <rect
          x="60" y="186"
          width="140" height="96"
          rx="22"
          fill="url(#shell)"
          stroke={ink}
          strokeWidth="2.5"
        />
        <rect
          x="60" y="186"
          width="140" height="96"
          rx="22"
          fill="url(#shellSide)"
        />

        {/* Chest panel — centered (76..184 → center 130, y: 208..266) */}
        <rect x="76" y="208" width="108" height="58" rx="10" fill="url(#chest)" />
        <rect x="76" y="208" width="108" height="58" rx="10" fill="none" stroke={ink} strokeWidth="0.8" opacity="0.4" />

        {/* W mark — perfectly centered in new chest panel (center 130, 237) */}
        <g transform="translate(103.25, 216.65) scale(0.22)" fill="#FFFFFF">
          <path
            d="m350.65,0l6.6,3.68 6.95,3.88 -77.36,89.01 c-3.37,3.88 -9.26,4.3 -13.14,0.93 l-24.85,-21.53 c-0.95,-0.82 -2.37,-0.78 -3.27,0.1 l-96.18,94.77 c-3.93,3.87 -10.58,1.7 -11.47,-3.74 l-17.86,-109.21 4.17,-1.73 12.5,46.84 c0.56,2.11 3.09,2.97 4.83,1.64 l70.54,-54.25 c5.07,-3.9 12.21,-3.59 16.92,0.74 l24.1,22.13 c0.79,0.73 1.99,0.78 2.84,0.12 L350.65,0Z"
            transform="translate(-120, 0)"
          />
        </g>

        {/* Status LED */}
        <circle cx="190" cy="196" r="2.5" fill={lime}>
          <animate attributeName="opacity" values="1;0.3;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* === ROCKET BOOSTER 🚀 === */}
      <g>
        {/* Booster collar */}
        <rect x="106" y="282" width="48" height="10" rx="3" fill={ink} opacity="0.9" />
        {/* Booster body */}
        <path
          d="M 102 292 L 158 292 L 152 320 L 108 320 Z"
          fill="url(#shell)"
          stroke={ink}
          strokeWidth="2"
        />
        {/* Booster vertical fins */}
        <line x1="116" y1="294" x2="113" y2="318" stroke={ink} strokeWidth="1" opacity="0.4" />
        <line x1="130" y1="294" x2="130" y2="318" stroke={ink} strokeWidth="1" opacity="0.4" />
        <line x1="144" y1="294" x2="147" y2="318" stroke={ink} strokeWidth="1" opacity="0.4" />
        {/* Nozzle */}
        <path d="M 108 320 L 152 320 L 146 332 L 114 332 Z" fill={ink} />
        <ellipse cx="130" cy="332" rx="16" ry="2.5" fill="#0A0830" />
      </g>

      {/* === FLAME === */}
      <g filter="url(#flameBlur)">
        <path
          d="M 116 332
             Q 122 344, 124 352
             Q 126 360, 130 366
             Q 134 360, 136 352
             Q 138 344, 144 332 Z"
          fill="url(#flame)"
          opacity="0.85"
        >
          <animate
            attributeName="d"
            values="M 116 332 Q 122 344, 124 352 Q 126 360, 130 366 Q 134 360, 136 352 Q 138 344, 144 332 Z;
                    M 116 332 Q 120 346, 122 356 Q 126 364, 130 370 Q 134 364, 138 356 Q 140 346, 144 332 Z;
                    M 116 332 Q 122 344, 124 352 Q 126 360, 130 366 Q 134 360, 136 352 Q 138 344, 144 332 Z"
            dur="0.5s"
            repeatCount="indefinite"
          />
        </path>
        <path
          d="M 121 332
             Q 124 342, 126 350
             Q 128 356, 130 360
             Q 132 356, 134 350
             Q 136 342, 139 332 Z"
          fill="#FFFFFF"
          opacity="0.75"
        >
          <animate attributeName="opacity" values="0.75;0.5;0.95;0.75" dur="0.35s" repeatCount="indefinite" />
        </path>
      </g>

      {/* Spark particles */}
      <circle cx="118" cy="346" r="1.2" fill="#EEE316">
        <animate attributeName="cy" values="346;358" dur="0.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0" dur="0.6s" repeatCount="indefinite" />
      </circle>
      <circle cx="142" cy="344" r="1" fill="#F3A114">
        <animate attributeName="cy" values="344;356" dur="0.7s" repeatCount="indefinite" begin="0.2s" />
        <animate attributeName="opacity" values="1;0" dur="0.7s" repeatCount="indefinite" begin="0.2s" />
      </circle>
      <circle cx="130" cy="350" r="1" fill="#E62C86">
        <animate attributeName="cy" values="350;364" dur="0.5s" repeatCount="indefinite" begin="0.1s" />
        <animate attributeName="opacity" values="1;0" dur="0.5s" repeatCount="indefinite" begin="0.1s" />
      </circle>
    </svg>
  );
}
